/* iter-p3-h — Reader Languages Insight panel.
 *
 * Shows the distribution of `preferred_language` across bookstore
 * customers with a horizontal bar chart, and pairs it against the
 * catalog's own per-language book count so admins can spot gaps
 * (many Spanish readers, few Spanish books → translate more).
 *
 * Route: /admin/reader-languages
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Languages, Loader2, RefreshCw, Wand2, CheckCircle2, Mail, Plus, Trash2, Heart, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { adminAuth, adminBookstoreInsightsApi } from '@/lib/api';

const LANG_LABELS = {
  en: 'English',    es: 'Español',   ar: 'العربية',   zh: '中文',
  fr: 'Français',   de: 'Deutsch',   pt: 'Português', ja: '日本語',
  ko: '한국어',     ru: 'Русский',   hi: 'हिन्दी',   it: 'Italiano',
  nl: 'Nederlands', pl: 'Polski',    sv: 'Svenska',   tr: 'Türkçe',
  he: 'עברית',      unset: 'Not set',
};

export default function ReaderLanguagesInsightPanel() {
  const [stats, setStats] = useState(null);
  const [books, setBooks] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy]   = useState(false);
  const [commissioning, setCommissioning] = useState(null); // language code being commissioned
  // iter-p3-k — Admin-editable vendor email (no more env-var round trip).
  const [vendorEmail, setVendorEmail]   = useState('');
  const [vendorSaved, setVendorSaved]   = useState('');
  const [vendorEnvFallback, setVendorEnvFallback] = useState(null);
  const [vendorEffective, setVendorEffective]     = useState(null);
  const [vendorBusy, setVendorBusy] = useState(false);
  // iter-p3-l — Per-language vendor routing table.
  const [routes, setRoutes] = useState({});
  const [newLang, setNewLang] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [routeBusy, setRouteBusy] = useState(false);
  // iter-p3-l — Aggregated wishlist demand.
  const [pending, setPending] = useState([]);
  const [notifying, setNotifying] = useState(null);

  const load = () => {
    setBusy(true);
    Promise.all([
      adminBookstoreInsightsApi.readerLanguages().then(setStats).catch(() => toast.error('Could not load reader stats.')),
      adminBookstoreInsightsApi.booksByLanguage().then(setBooks).catch(() => {}),
      adminBookstoreInsightsApi.listTasks('open').then((r) => setTasks(r?.tasks || [])).catch(() => {}),
      adminBookstoreInsightsApi.getVendorEmail().then((r) => {
        setVendorEmail(r?.email || '');
        setVendorSaved(r?.email || '');
        setVendorEnvFallback(r?.env_fallback || null);
        setVendorEffective(r?.effective || null);
        setRoutes(r?.routes || {});
      }).catch(() => {}),
      adminBookstoreInsightsApi.pendingWishlists().then((r) => {
        setPending(r?.pending || []);
      }).catch(() => {}),
    ]).finally(() => setBusy(false));
  };

  const saveRoute = async (lang, email) => {
    const trimmed = (lang || '').trim().toLowerCase();
    if (!trimmed) { toast.error('Language code required.'); return; }
    setRouteBusy(true);
    try {
      const r = await adminBookstoreInsightsApi.upsertVendorRoute(trimmed, email.trim());
      setRoutes(r?.routes || {});
      toast.success(email.trim()
        ? `Route saved: ${trimmed.toUpperCase()} → ${email.trim()}`
        : `Route cleared for ${trimmed.toUpperCase()}.`);
      if (!email.trim()) return;
      if (trimmed === (newLang || '').trim().toLowerCase()) {
        setNewLang(''); setNewEmail('');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save route.');
    } finally { setRouteBusy(false); }
  };

  const removeRoute = async (lang) => {
    setRouteBusy(true);
    try {
      const r = await adminBookstoreInsightsApi.deleteVendorRoute(lang);
      setRoutes(r?.routes || {});
      toast.success(`Removed route ${lang.toUpperCase()}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove route.');
    } finally { setRouteBusy(false); }
  };

  const notifyWishlist = async (row) => {
    setNotifying(row.book_id + ':' + (row.language || ''));
    try {
      const r = await adminBookstoreInsightsApi.notifyWishlist(row.book_id);
      toast.success(`Notified ${r?.notified || 0} reader${r?.notified === 1 ? '' : 's'} (${r?.matched || 0} matched).`);
      adminBookstoreInsightsApi.pendingWishlists().then((rr) => setPending(rr?.pending || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not notify wishlisters.');
    } finally { setNotifying(null); }
  };

  const saveVendorEmail = async () => {
    setVendorBusy(true);
    try {
      const r = await adminBookstoreInsightsApi.setVendorEmail(vendorEmail.trim());
      setVendorSaved(r?.email || '');
      setVendorEffective(r?.effective || null);
      toast.success(r?.email
        ? `Translation vendor set to ${r.email}.`
        : 'Vendor override cleared — falling back to env var.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save vendor email.');
    } finally { setVendorBusy(false); }
  };

  const commission = async (row, booksCount) => {
    setCommissioning(row.code);
    try {
      const r = await adminBookstoreInsightsApi.commissionTranslation({
        language_code: row.code,
        reader_count:  row.count,
        book_count:    booksCount || 0,
      });
      if (r?.created) toast.success(`Task queued for ${row.label || row.code}.`);
      else toast.success(`Already queued for ${row.label || row.code}.`);
      adminBookstoreInsightsApi.listTasks('open').then((rr) => setTasks(rr?.tasks || []));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not commission.'); }
    finally { setCommissioning(null); }
  };

  const markCompleted = async (task) => {
    try {
      await adminBookstoreInsightsApi.updateTask(task.id, 'completed');
      toast.success(`Marked ${task.language_code} translation completed.`);
      adminBookstoreInsightsApi.listTasks('open').then((rr) => setTasks(rr?.tasks || []));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update task.'); }
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && !adminAuth.user) return;
    load();
  }, []);

  const rows = stats?.languages || [];
  const maxCount = Math.max(1, ...rows.map((r) => r.count || 0));

  // Map of code → book count for the gap panel.
  const bookCountByLang = useMemo(() => {
    const map = {};
    (books?.languages || []).forEach((r) => { map[r.code || 'unset'] = r.count; });
    return map;
  }, [books]);

  // Codes with an open commission task.
  const queuedByLang = useMemo(() => {
    const s = new Set();
    (tasks || []).forEach((t) => s.add(t.language_code));
    return s;
  }, [tasks]);

  if (typeof window !== 'undefined' && !adminAuth.user) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="bg-background text-foreground" data-testid="admin-reader-languages-page">
      <header className="flex items-center justify-between border-b border-border bg-panel-header/40 px-4 py-2">
        <div className="flex items-center gap-2">
          <Link to="/admin">
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]">
              <ArrowLeft className="h-3.5 w-3.5" /> Admin Hub
            </Button>
          </Link>
          <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary text-primary-foreground">
            <Languages className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Admin · Bookstore</div>
            <div className="text-[13px] font-semibold leading-tight">Reader Languages</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy} className="h-7 gap-1.5 text-[12px]" data-testid="reader-languages-refresh">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
        </Button>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 space-y-4">
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Bookstore customer base</div>
              <div className="text-[15px] font-semibold">
                {stats?.total_users ?? 0} readers · {rows.length} language buckets
              </div>
            </div>
          </div>
          {!stats ? (
            <div className="p-4 text-[12px] text-muted-foreground"><Loader2 className="inline h-3.5 w-3.5 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-[12.5px] text-muted-foreground" data-testid="reader-languages-empty">
              No readers with a preferred language set yet. The bar chart populates as customers pick their reading language from Account → Reading language.
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="reader-languages-chart">
              {rows.map((r) => {
                const width = (r.count / maxCount) * 100;
                const books = bookCountByLang[r.code];
                // Highlight rows where reader demand ≥ 5 readers AND book supply < 3.
                const isGap = (r.code !== 'unset') && (r.count >= 5) && ((books ?? 0) < 3);
                return (
                  <div key={r.code} data-testid={`reader-languages-row-${r.code}`} className={`rounded-sm p-2 ${isGap ? 'bg-amber-50/60 dark:bg-amber-900/10' : ''}`}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-[11.5px]">
                      <span className="font-medium">
                        {LANG_LABELS[r.code] || r.label || r.code}
                        {isGap && (
                          <span className="ml-2 rounded bg-amber-500 px-1.5 py-[1px] text-[9.5px] font-bold uppercase text-white" data-testid={`reader-languages-gap-${r.code}`}>
                            translation gap
                          </span>
                        )}
                        {queuedByLang.has(r.code) && (
                          <span className="ml-2 rounded bg-primary px-1.5 py-[1px] text-[9.5px] font-bold uppercase text-primary-foreground" data-testid={`reader-languages-queued-${r.code}`}>
                            queued
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono-stat text-muted-foreground">
                          {r.count} · {r.pct}%
                          {books !== undefined && (
                            <span className="ml-2 opacity-70">({books} book{books === 1 ? '' : 's'})</span>
                          )}
                        </span>
                        {isGap && !queuedByLang.has(r.code) && (
                          <button
                            type="button"
                            onClick={() => commission(r, books ?? 0)}
                            disabled={commissioning === r.code}
                            data-testid={`reader-languages-commission-${r.code}`}
                            className="inline-flex items-center gap-1 rounded-sm border border-amber-500 bg-amber-500 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-white hover:bg-amber-600 disabled:opacity-60"
                          >
                            {commissioning === r.code ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                            Commission
                          </button>
                        )}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-sm bg-panel-header">
                      <div
                        className={`h-full transition-all ${isGap ? 'bg-amber-500' : 'bg-primary'}`}
                        style={{ width: `${Math.max(2, width)}%` }}
                        data-testid={`reader-languages-bar-${r.code}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-4" data-testid="reader-languages-vendor-email-card">
          <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            <Mail className="h-3.5 w-3.5" /> Translation vendor routing
          </div>
          <p className="mb-3 text-[12px] text-muted-foreground">
            Route each language's <b>Commission</b> brief + CSV to the right agency. Add a <code>default</code> row to catch anything without a specific match. When no routes match, the legacy single-vendor field (below) is used, then the <code>TRANSLATION_VENDOR_EMAIL</code> env var.
          </p>
          <div className="space-y-1.5" data-testid="reader-languages-vendor-routes-list">
            {Object.keys(routes).length === 0 && (
              <div className="rounded-md border border-dashed border-border p-3 text-center text-[11.5px] text-muted-foreground" data-testid="reader-languages-vendor-routes-empty">
                No per-language routes yet. Add one below (e.g. <code>es → spanish@your-agency.com</code>).
              </div>
            )}
            {Object.entries(routes).sort().map(([lang, email]) => (
              <div key={lang} className="flex flex-wrap items-center gap-2 rounded-sm border border-border bg-panel-header/20 px-2 py-1.5 text-[12px]" data-testid={`reader-languages-vendor-route-${lang}`}>
                <span className="min-w-[64px] rounded bg-primary/10 px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-primary">
                  {lang}
                </span>
                <span className="flex-1 truncate font-mono-stat text-[11.5px]">{email}</span>
                <span className="text-[10.5px] text-muted-foreground">
                  {LANG_LABELS[lang] || (lang === 'default' ? 'Everything else' : lang)}
                </span>
                <button
                  type="button"
                  onClick={() => removeRoute(lang)}
                  disabled={routeBusy}
                  className="rounded-sm border border-border p-1 text-muted-foreground hover:border-oxblood/40 hover:text-oxblood"
                  data-testid={`reader-languages-vendor-route-remove-${lang}`}
                  title={`Remove ${lang} route`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={newLang}
              onChange={(e) => setNewLang(e.target.value)}
              placeholder="lang code (es, ar, default…)"
              className="w-[180px] rounded-sm border border-border bg-panel-header/20 px-2 py-1 text-[12px]"
              data-testid="reader-languages-vendor-route-new-lang"
              disabled={routeBusy}
            />
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="agency@vendor.com"
              className="min-w-[220px] flex-1 rounded-sm border border-border bg-panel-header/20 px-2 py-1 text-[12px]"
              data-testid="reader-languages-vendor-route-new-email"
              disabled={routeBusy}
            />
            <Button
              size="sm"
              onClick={() => saveRoute(newLang, newEmail)}
              disabled={routeBusy || !newLang.trim() || !newEmail.trim()}
              className="h-7 gap-1.5 text-[12px]"
              data-testid="reader-languages-vendor-route-add"
            >
              {routeBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add route
            </Button>
          </div>
          <details className="mt-4">
            <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-muted-foreground">Legacy single vendor (fallback)</summary>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                type="email"
                value={vendorEmail}
                onChange={(e) => setVendorEmail(e.target.value)}
                placeholder={vendorEnvFallback ? `env fallback: ${vendorEnvFallback}` : 'legacy vendor email'}
                className="min-w-[240px] flex-1 rounded-sm border border-border bg-panel-header/20 px-2 py-1 text-[12px]"
                data-testid="reader-languages-vendor-email-input"
                disabled={vendorBusy}
              />
              <Button
                size="sm"
                onClick={saveVendorEmail}
                disabled={vendorBusy || vendorEmail.trim() === vendorSaved.trim()}
                className="h-7 gap-1.5 text-[12px]"
                data-testid="reader-languages-vendor-email-save"
              >
                {vendorBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save
              </Button>
            </div>
          </details>
          <div className="mt-2 text-[11px] text-muted-foreground" data-testid="reader-languages-vendor-email-effective">
            Default handoff resolves to: <b>{vendorEffective || 'nobody (task will queue but no email is sent)'}</b>
          </div>
        </Card>

        <Card className="p-4" data-testid="reader-languages-wishlist-card">
          <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            <Heart className="h-3.5 w-3.5" /> Pending wishlist demand
          </div>
          <p className="mb-3 text-[12px] text-muted-foreground">
            Which titles readers have asked for, grouped by the language they're waiting in. Click <b>Notify</b> after that book&rsquo;s translation is live to email everyone in line.
          </p>
          {pending.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground" data-testid="reader-languages-wishlist-empty">
              No open wishlists yet. Readers will land here after they tap the heart on any book detail modal.
            </div>
          ) : (
            <ul className="space-y-1" data-testid="reader-languages-wishlist-list">
              {pending.slice(0, 20).map((row) => {
                const key = row.book_id + ':' + (row.language || '');
                const bookLang = (row.book?.language_code || '').split('-')[0].toLowerCase();
                const ready = !row.language || row.language === bookLang;
                return (
                  <li key={key} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-border bg-panel-header/20 px-2 py-1.5 text-[12px]" data-testid={`reader-languages-wishlist-row-${key}`}>
                    <span className="min-w-0 flex-1 truncate">
                      <b className="truncate">{row.book?.title || row.book_id}</b>
                      <span className="ml-2 rounded bg-primary/10 px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wider text-primary">
                        {row.language ? row.language.toUpperCase() : 'ANY'}
                      </span>
                      <span className="ml-2 text-[10.5px] text-muted-foreground">
                        {row.count} reader{row.count === 1 ? '' : 's'}
                        {row.book?.language_code && (
                          <span className="ml-1 opacity-70">· book is {bookLang.toUpperCase()}</span>
                        )}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => notifyWishlist(row)}
                      disabled={notifying === key || !ready}
                      title={ready ? 'Send the ready-notification email' : 'Book is not yet available in that language'}
                      data-testid={`reader-languages-wishlist-notify-${key}`}
                      className="inline-flex items-center gap-1 rounded-sm border border-emerald-500 bg-emerald-500 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-white hover:bg-emerald-600 disabled:opacity-40"
                    >
                      {notifying === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      Notify
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>


        <Card className="p-4" data-testid="reader-languages-tasks-card">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Open commission tasks ({tasks.length})</div>
          {tasks.length === 0 ? (
            <div className="mt-2 text-[12px] text-muted-foreground">No open tasks. Commission gaps above to queue work.</div>
          ) : (
            <ul className="mt-2 space-y-1">
              {tasks.map((t) => (
                <li key={t.id} className="rounded-sm border border-border bg-panel-header/20 px-2 py-1.5 text-[12px]" data-testid={`reader-languages-task-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {LANG_LABELS[t.language_code] || t.language_code}
                      <span className="ml-2 text-[10.5px] text-muted-foreground">
                        {t.reader_count} readers · {t.book_count} books · queued {new Date(t.created_at).toLocaleDateString()}
                      </span>
                      {t.status === 'quote_received' && (
                        <span className="ml-2 rounded bg-emerald-500 px-1.5 py-[1px] text-[9.5px] font-bold uppercase text-white" data-testid={`reader-languages-task-quote-${t.id}`}>
                          quote in
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => markCompleted(t)}
                      data-testid={`reader-languages-task-done-${t.id}`}
                      className="inline-flex items-center gap-1 rounded-sm border border-emerald-500 bg-emerald-500 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-white hover:bg-emerald-600"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Mark done
                    </button>
                  </div>
                  {(t.quote_rate_per_word_cents != null || t.quote_lead_days != null) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-sm bg-emerald-50/50 px-2 py-1 text-[11px] text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200" data-testid={`reader-languages-task-quote-details-${t.id}`}>
                      <span className="uppercase tracking-wider opacity-70">Quote:</span>
                      {t.quote_rate_per_word_cents != null && (
                        <b>{(t.quote_rate_per_word_cents / 100).toFixed(2)} {t.quote_currency || 'USD'} / word</b>
                      )}
                      {t.quote_lead_days != null && (
                        <span>· {t.quote_lead_days}-day turnaround</span>
                      )}
                      {t.quote_confidence && (
                        <span className="rounded bg-emerald-500/20 px-1.5 py-[1px] text-[9.5px] font-bold uppercase">
                          {t.quote_confidence} confidence
                        </span>
                      )}
                      {t.vendor_reply_latest?.from_addr && (
                        <span className="ml-auto opacity-70">from {t.vendor_reply_latest.from_addr}</span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Reading the chart</div>
          <ul className="mt-2 space-y-1 text-[12px] text-muted-foreground">
            <li><b>Translation gap</b> marker fires when 5+ readers pick a language but the catalog has fewer than 3 titles in it — commission a translation before those readers churn.</li>
            <li><b>Not set</b> is the tail of customers who haven't picked a language in Account → Reading language yet; they still receive emails in the book's own language.</li>
            <li>Book counts fold subtag variants together, so <code>es-MX</code> and <code>es-419</code> both count toward <code>es</code>.</li>
          </ul>
        </Card>
      </main>
    </div>
  );
}
